import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Activity, Database, HardDrive, RefreshCw, CheckCircle2, AlertTriangle, Clock, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import BulkAgentDocumentRecovery from './BulkAgentDocumentRecovery';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const formatBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return 'never';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const StatusBadge = ({ ok, label }) => (
  <Badge className={ok ? 'bg-emerald-500 text-white hover:bg-emerald-500' : 'bg-rose-500 text-white hover:bg-rose-500'}>
    {ok ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
    {label}
  </Badge>
);

// Tiny inline SVG sparkline — shows recent backup sizes so admins can see trend at a glance.
const Sparkline = ({ values, width = 200, height = 40 }) => {
  if (!values || values.length === 0) {
    return <div className="text-xs text-gray-400">No history yet</div>;
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline fill="none" stroke="#10b981" strokeWidth="2" points={points} />
      {values.map((v, i) => (
        <circle
          key={i}
          cx={(i * step).toFixed(1)}
          cy={(height - ((v - min) / range) * height).toFixed(1)}
          r="2.5"
          fill="#10b981"
        />
      ))}
    </svg>
  );
};

const SystemHealthTab = ({ preFilterAgentId = '' }) => {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, m, s] = await Promise.all([
        axios.get(`${API_URL}/api/health`).then(r => r.data).catch(() => null),
        axios.get(`${API_URL}/api/admin/metrics`, { headers: authHeader() }).then(r => r.data),
        axios.get(`${API_URL}/api/admin/storage/health`, { headers: authHeader() }).then(r => r.data),
      ]);
      setHealth(h);
      setMetrics(m);
      setStorage(s);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Unable to load system health';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const runBackupNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/backup/run`, {}, { headers: authHeader() });
      if (data?.status === 'ok') {
        toast.success(`Backup completed: ${data.documents?.toLocaleString() || 0} docs, ${formatBytes(data.size_bytes)}`);
      } else {
        toast.error(`Backup ${data?.status || 'failed'}: ${data?.error || 'unknown error'}`);
      }
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Backup failed';
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const lastBackup = metrics?.last_backup;
  const backupOk = lastBackup?.status === 'ok';
  const dbOk = health?.checks?.mongo === 'ok';
  const storageOk = storage?.init_ok && storage?.roundtrip_ok;
  const history = metrics?.backup_history || [];
  const historyValues = history.map(h => h.size_bytes || 0);

  const collections = metrics?.collections || {};
  const importantCollections = ['leads', 'applications', 'agents', 'students', 'employees', 'universities', 'courses', 'agent_compliance_documents'];

  return (
    <div className="space-y-6" data-testid="system-health-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-teal-600" />
            System Health
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Live snapshot of database, backups, and document storage. Refresh anytime.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runBackupNow} disabled={running || loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="system-health-run-backup-btn">
            <PlayCircle className={`w-4 h-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Running…' : 'Run Backup Now'}
          </Button>
          <Button onClick={fetchAll} disabled={loading} variant="outline" data-testid="system-health-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-rose-200 bg-rose-50 dark:bg-rose-950/30">
          <CardContent className="pt-6 text-rose-700 dark:text-rose-300 text-sm">
            {error}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Database health */}
        <Card data-testid="health-card-database">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Database className="w-4 h-4" />
              Database
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-semibold text-gray-800 dark:text-white">MongoDB</span>
              <StatusBadge ok={dbOk} label={dbOk ? 'Healthy' : 'Down'} />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {metrics?.total_collections ?? '—'} collections active
            </p>
          </CardContent>
        </Card>

        {/* Backup status — the headline number */}
        <Card data-testid="health-card-backup" className={backupOk ? 'border-emerald-200' : 'border-rose-200'}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Last Backup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-semibold text-gray-800 dark:text-white">
                {lastBackup ? formatBytes(lastBackup.size_bytes) : '—'}
              </span>
              <StatusBadge ok={backupOk} label={backupOk ? 'OK' : (lastBackup ? 'Failed' : 'Pending')} />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {lastBackup ? formatDate(lastBackup.completed_at) : 'No backup yet — first run at 02:00 UTC'}
            </p>
            {lastBackup?.documents != null && (
              <p className="text-xs text-gray-500 mt-1">
                {Number(lastBackup.documents).toLocaleString()} documents archived
              </p>
            )}
            {lastBackup?.error && (
              <p className="text-xs text-rose-600 dark:text-rose-400 mt-1 break-all">{lastBackup.error}</p>
            )}
          </CardContent>
        </Card>

        {/* Document storage */}
        <Card data-testid="health-card-storage">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <HardDrive className="w-4 h-4" />
              Document Storage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-semibold text-gray-800 dark:text-white">
                {storage?.counts?.compliance_with_storage ?? '—'}
              </span>
              <StatusBadge ok={storageOk} label={storageOk ? 'Healthy' : 'Issue'} />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              files persisted (out of {storage?.counts?.agent_compliance_documents ?? '—'})
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 7-day backup history sparkline */}
      <Card data-testid="health-card-backup-history">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>7-Day Backup History</span>
            <span className="text-xs font-normal text-gray-500">{history.length} run{history.length === 1 ? '' : 's'}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Sparkline values={historyValues} />
          {history.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {history.slice(-4).reverse().map((h, i) => (
                <div key={i} className="p-2 rounded bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700">
                  <div className="text-gray-500">{formatDate(h.completed_at)}</div>
                  <div className="font-semibold text-gray-700 dark:text-gray-200">
                    {formatBytes(h.size_bytes)} · {Number(h.documents || 0).toLocaleString()} docs
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Record counts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Live Record Counts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {importantCollections.map((key) => (
              <div key={key} className="p-3 rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700">
                <div className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</div>
                <div className="text-xl font-semibold text-gray-800 dark:text-white">
                  {(collections[key] ?? 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bulk agent document recovery — embedded recovery tool */}
      <BulkAgentDocumentRecovery preFilterAgentId={preFilterAgentId} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">What this page tells you</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
          <p><b>Database</b> — should always say "Healthy". If it ever says "Down", contact support immediately.</p>
          <p><b>Last Backup</b> — should say "OK" with a timestamp from earlier today. The backup runs automatically at 02:00 UTC every day. Press <b>Run Backup Now</b> to trigger an immediate backup. If it ever fails you'll also receive an email at <span className="font-mono">info@hedirect.org</span>.</p>
          <p><b>Document Storage</b> — files persisted vs total records. New uploads survive container redeploys.</p>
          <p><b>Bulk Recovery</b> — drag in a folder of legacy agent documents and we auto-match them to the right agent + document type. Review the suggestions, then upload them all at once.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealthTab;
